import Loader from '../../common/Loader'
import { Link } from 'react-router'
import { useOrgAuth } from '../../OrgAuthProvider.ts'
import { useCreateAssessment, useGetAssessments, useGetOrganizationUsers } from '../../http/hooks.ts'
import { getMe } from '../../MeProvider.ts'
import { IAssessment, IUser } from '../../services/nextpeakClient'
import { Page, PageProps } from '../../layout/Page.tsx'
import Table, { validateTableProps } from '../../newComponents/Table/index.tsx'
import { TableDataCell, TableHeaderCell } from '../../newComponents/Table/Cell.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { isAssessmentFilledOutByManager, isAssessmentFilledOutByUser } from '../../utils.ts'
import { getAssessmentResponseByUserId } from '../Assessments/utils.ts'
import { Button } from '../../newComponents/Button/Button.tsx'
import { toast } from 'react-toastify'
import { useCallback } from 'react'
import { PlusCircle } from 'lucide-react'

export const EmployeesTable = ({
	employees,
	pendingAssessments,
	onCreateAssessment,
}: {
	employees: IUser[]
	pendingAssessments?: IAssessment[]
	onCreateAssessment?: (employeeId: string) => void
}) => {
	const me = getMe()

	const tableProps = validateTableProps(
		[
			{
				key: 'name',
				content: <TableHeaderCell>Name</TableHeaderCell>,
			},
			{
				key: 'email',
				content: <TableHeaderCell>Email</TableHeaderCell>,
			},
			{
				key: 'assessment',
				content: <TableHeaderCell>Assessment</TableHeaderCell>,
			},
			{
				key: 'action',
				content: <TableHeaderCell>User Profile</TableHeaderCell>,
			},
		] as const,
		employees.map((employee) => {
			// Find pending assessment for this employee
			const pendingAssessment = pendingAssessments?.find(
				(assessment) =>
					assessment.employeeUserId === employee.id &&
					!(isAssessmentFilledOutByUser(assessment) && isAssessmentFilledOutByManager(assessment))
			)

			// Get the appropriate response based on user role
			let responseLink = ''
			if (pendingAssessment && me.id) {
				const myResponse = getAssessmentResponseByUserId(pendingAssessment, me.id)
				if (myResponse) {
					responseLink = `/assessments/${pendingAssessment.id}/responses/${myResponse.id}`
				} else {
					responseLink = `/assessments/${pendingAssessment.id}`
				}
			}

			return {
				name: <TableDataCell>{employee.name}</TableDataCell>,
				email: <TableDataCell>{employee.email}</TableDataCell>,
				assessment: (
					<TableDataCell>
						{pendingAssessment && responseLink && (
							<Link className="hover:text-primary flex" to={responseLink}>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="size-5 mr-1"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15.91 11.672a.375.375 0 0 1 0 .656l-5.603 3.113a.375.375 0 0 1-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112Z"
									/>
								</svg>
								Take Assessment
							</Link>
						)}
						{!pendingAssessment && onCreateAssessment && (
							<Button onClick={() => onCreateAssessment(employee.id!)} color="primary" size="sm" className="px-2 py-1 text-xs">
								<PlusCircle className="size-4 mr-1" />
								Create Assessment
							</Button>
						)}
					</TableDataCell>
				),
				action: (
					<TableDataCell>
						<Link className="hover:text-primary" to={'/employees/' + employee.id}>
							<svg className="fill-current" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path
									d="M8.99981 14.8219C3.43106 14.8219 0.674805 9.50624 0.562305 9.28124C0.47793 9.11249 0.47793 8.88749 0.562305 8.71874C0.674805 8.49374 3.43106 3.20624 8.99981 3.20624C14.5686 3.20624 17.3248 8.49374 17.4373 8.71874C17.5217 8.88749 17.5217 9.11249 17.4373 9.28124C17.3248 9.50624 14.5686 14.8219 8.99981 14.8219ZM1.85605 8.99999C2.4748 10.0406 4.89356 13.5562 8.99981 13.5562C13.1061 13.5562 15.5248 10.0406 16.1436 8.99999C15.5248 7.95936 13.1061 4.44374 8.99981 4.44374C4.89356 4.44374 2.4748 7.95936 1.85605 8.99999Z"
									fill=""
								/>
								<path
									d="M9 11.3906C7.67812 11.3906 6.60938 10.3219 6.60938 9C6.60938 7.67813 7.67812 6.60938 9 6.60938C10.3219 6.60938 11.3906 7.67813 11.3906 9C11.3906 10.3219 10.3219 11.3906 9 11.3906ZM9 7.875C8.38125 7.875 7.875 8.38125 7.875 9C7.875 9.61875 8.38125 10.125 9 10.125C9.61875 10.125 10.125 9.61875 10.125 9C10.125 8.38125 9.61875 7.875 9 7.875Z"
									fill=""
								/>
							</svg>
						</Link>
					</TableDataCell>
				),
			}
		})
	)

	return (
		<Card>
			<Table {...tableProps} />
		</Card>
	)
}

const pageProps: PageProps = {
	title: 'Employees',
	breadcrumbs: 'Employees',
}

const EmployeesWithOrgContext = ({ activeOrganizationId }: { activeOrganizationId: string }) => {
	const me = getMe()
	const client = useNextPeakClient()
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()

	const { data: employees, isLoading } = useGetOrganizationUsers(
		client,
		{ organizationId: activeOrganizationId, managerId: me.id, includes: ['organizationRoles'] },
		{
			enabled: !!activeOrganizationId,
		}
	)

	// Fetch all assessments for employees under this manager with pagination
	const {
		data: assessments,
		isLoading: isLoadingAssessments,
		refetch: refetchAssessments,
	} = useGetAssessments(
		client,
		{
			managerUserId: me.id,
			includes: ['assessmentResponses'],
		},
		{
			enabled: !!activeOrganizationId && !!employees,
		}
	)

	const mutation = useCreateAssessment(client)

	const handleCreateAssessment = useCallback(
		async (employeeId: string) => {
			try {
				if (!me.id) {
					toast.error('You need to be logged in')
					return
				}

				const data = {
					employeeUserId: employeeId,
					managerUserId: me.id,
					organizationId: orgId,
				}

				await mutation.mutateAsync(data)
				toast.success('Assessment created')
				refetchAssessments()
			} catch (e) {
				console.error(e)
				toast.error('Failed to create assessment')
			}
		},
		[mutation, me.id, orgId, refetchAssessments]
	)

	return (
		<Page {...pageProps}>
			<div className="flex flex-col gap-10">
				{(isLoading || isLoadingAssessments) && (
					<Card>
						<Loader />
					</Card>
				)}
				{!isLoading && employees && (
					<EmployeesTable employees={employees as IUser[]} pendingAssessments={assessments} onCreateAssessment={handleCreateAssessment} />
				)}
			</div>
		</Page>
	)
}

const Employees = () => {
	const {
		authOrgUser: { orgId },
	} = useOrgAuth()

	if (!orgId) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>You need to select an active organization first</div>
				</div>
			</Page>
		)
	}

	return <EmployeesWithOrgContext activeOrganizationId={orgId} />
}

export default Employees
